import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Badge, Form, InputGroup, Button, Spinner } from 'react-bootstrap';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import ArticlesOffcanvas from './ArticlesOffcanvas';
import '../SearchMain.css';

const config = require('../../../config.json');

export default function JournalOffcanvas(props) {
  const { selectedBook, idToken: propIdToken } = props;
  const journal = selectedBook || {};
  const isBoundJournal = (journal.journalType || '').trim().toLowerCase() === 'bound journal';

  // Active view tab: 'overview' | 'issues' | 'articles'
  const [activeTab, setActiveTab] = useState('overview');

  // Issues State
  const [issues, setIssues] = useState([]);
  const [loadingIssues, setLoadingIssues] = useState(false);
  const [issuesError, setIssuesError] = useState(null);
  const [issueSearchTerm, setIssueSearchTerm] = useState('');
  const [issueStatusFilter, setIssueStatusFilter] = useState('ALL');
  const [issueVolumeFilter, setIssueVolumeFilter] = useState('ALL');

  // Articles State
  const [articles, setArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [articlesError, setArticlesError] = useState(null);
  const [articleSearchTerm, setArticleSearchTerm] = useState('');
  const [articleVolFilter, setArticleVolFilter] = useState('ALL');
  const [selectedArticleDetail, setSelectedArticleDetail] = useState(null);

  // Citation Copy Feedback State
  const [copiedCitationId, setCopiedCitationId] = useState(null);

  // Helper to obtain authorization header
  const getAuthHeaders = useCallback(async () => {
    let token = propIdToken;
    if (!token) {
      try {
        const session = await fetchAuthSession();
        token = session?.tokens?.idToken?.toString();
      } catch (err) {
        // Unauthenticated session fallback
      }
    }
    return {
      ...(token ? { Authorization: token } : {}),
      'x-api-key': config.api.key
    };
  }, [propIdToken]);

  // Fetch Issues for the journal
  const loadIssues = useCallback(async (journalId) => {
    if (!journalId) return;
    try {
      setLoadingIssues(true);
      setIssuesError(null);
      const headers = await getAuthHeaders();
      const response = await axios.get(`${config.api.invokeURL}/vjlibrary/search/journal/issues`, {
        params: {
          fetchKey: journalId,
          fetchOption: 'JournalID',
          clientID: 'VJCollege'
        },
        headers
      });

      const data = Array.isArray(response.data) ? response.data : [];
      // Sort issues descending by volume, then issue number
      const sorted = [...data].sort((a, b) => {
        const volA = Number(a.volNo ?? a.volume) || 0;
        const volB = Number(b.volNo ?? b.volume) || 0;
        if (volA !== volB) return volB - volA;
        const issA = Number(a.issueNumber) || 0;
        const issB = Number(b.issueNumber) || 0;
        return issB - issA;
      });
      setIssues(sorted);
      setLoadingIssues(false);
    } catch (err) {
      console.error('Error fetching journal issues:', err);
      setIssuesError(err.response?.data?.message || err.message);
      setLoadingIssues(false);
    }
  }, [getAuthHeaders]);

  // Fetch Articles for the journal
  const loadArticles = useCallback(async (journalId) => {
    if (!journalId) return;
    try {
      setLoadingArticles(true);
      setArticlesError(null);
      const headers = await getAuthHeaders();
      const response = await axios.get(`${config.api.invokeURL}/vjlibrary/search/journal/articles`, {
        params: {
          fetchKey: journalId,
          fetchOption: 'JournalID',
          clientID: 'VJCollege'
        },
        headers
      });

      const data = Array.isArray(response.data) ? response.data : [];
      // Sort articles by volume desc, then article title asc
      const sorted = [...data].sort((a, b) => {
        const volA = Number(a.volissuesNo) || 0;
        const volB = Number(b.volissuesNo) || 0;
        if (volA !== volB) return volB - volA;
        return (a.articleTitle || '').localeCompare(b.articleTitle || '');
      });
      setArticles(sorted);
      setLoadingArticles(false);
    } catch (err) {
      console.error('Error fetching journal articles:', err);
      setArticlesError(err.response?.data?.message || err.message);
      setLoadingArticles(false);
    }
  }, [getAuthHeaders]);

  // Trigger loads whenever selected journal changes
  useEffect(() => {
    setActiveTab('overview');
    setSelectedArticleDetail(null);
    setIssueSearchTerm('');
    setIssueStatusFilter('ALL');
    setIssueVolumeFilter('ALL');
    setArticleSearchTerm('');
    setArticleVolFilter('ALL');

    if (journal.journalID) {
      loadIssues(journal.journalID);
      loadArticles(journal.journalID);
    } else {
      setIssues([]);
      setArticles([]);
    }
  }, [journal.journalID, loadIssues, loadArticles]);

  // Unique Volumes for Issues Filter
  const uniqueIssueVolumes = useMemo(() => {
    const vols = new Set();
    issues.forEach((item) => {
      const v = item.volNo ?? item.volume;
      if (v !== undefined && v !== null && v !== '') vols.add(v.toString());
    });
    return Array.from(vols).sort((a, b) => Number(b) - Number(a));
  }, [issues]);

  // Filtered Issues list
  const filteredIssues = useMemo(() => {
    return issues.filter((issue) => {
      // Free-text search matching
      if (issueSearchTerm.trim() !== '') {
        const term = issueSearchTerm.toLowerCase();
        const vol = (issue.volNo ?? issue.volume ?? '').toString().toLowerCase();
        const iss = (issue.issueNumber ?? '').toString().toLowerCase();
        const year = (issue.issueYear ?? issue.year ?? '').toString().toLowerCase();
        const month = (issue.issueMonth ?? issue.month ?? '').toString().toLowerCase();
        const status = (issue.issueStatus ?? '').toString().toLowerCase();
        const code = (issue.volissuesNo ?? '').toString().toLowerCase();

        const match =
          vol.includes(term) ||
          iss.includes(term) ||
          year.includes(term) ||
          month.includes(term) ||
          status.includes(term) ||
          code.includes(term) ||
          `vol ${vol}`.includes(term) ||
          `issue ${iss}`.includes(term);
        if (!match) return false;
      }

      // Status Filter
      if (issueStatusFilter !== 'ALL') {
        const status = (issue.issueStatus || 'Available').toLowerCase();
        if (issueStatusFilter === 'Received' && !['received', 'available', 'active'].includes(status)) return false;
        if (issueStatusFilter === 'Expected' && !['expected', 'pending'].includes(status)) return false;
        if (issueStatusFilter === 'Other' && ['received', 'available', 'active', 'expected', 'pending'].includes(status)) return false;
      }

      // Volume Filter
      if (issueVolumeFilter !== 'ALL') {
        const v = (issue.volNo ?? issue.volume ?? '').toString();
        if (v !== issueVolumeFilter) return false;
      }

      return true;
    });
  }, [issues, issueSearchTerm, issueStatusFilter, issueVolumeFilter]);

  // Article count map grouped by issue key (volissuesNo or vol-issue)
  const articleCountByIssue = useMemo(() => {
    const counts = {};
    articles.forEach((art) => {
      const key = (art.volissuesNo || '').toString().trim().toLowerCase();
      if (key) {
        counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  }, [articles]);

  // Unique Volumes / Issues for Articles Filter Dropdown
  const uniqueArticleVolumes = useMemo(() => {
    const vols = new Set();
    articles.forEach((item) => {
      const v = item.volissuesNo || item.internalVolIssueNo;
      if (v) vols.add(v.toString().trim());
    });
    return Array.from(vols).sort();
  }, [articles]);

  // Filtered Articles list
  const filteredArticles = useMemo(() => {
    return articles.filter((article) => {
      // Issue filter
      if (articleVolFilter !== 'ALL') {
        const volKey = articleVolFilter.toLowerCase().trim();
        const artVol = (article.volissuesNo || '').toString().toLowerCase().trim();
        const artIntVol = (article.internalVolIssueNo || '').toString().toLowerCase().trim();

        if (artVol !== volKey && artIntVol !== volKey && !artVol.includes(volKey)) {
          return false;
        }
      }

      // Free-text search
      if (articleSearchTerm.trim() !== '') {
        const term = articleSearchTerm.toLowerCase();
        const title = (article.articleTitle || '').toLowerCase();
        const subtitle = (article.articleSubTitle || '').toLowerCase();
        const author = (article.articleAuthor || '').toLowerCase();
        const kw = (article.articleKeywords || '').toLowerCase();
        const desc = (article.articleDescription || '').toLowerCase();
        const callNo = (article.callNo || '').toLowerCase();
        const vol = (article.volissuesNo || '').toString().toLowerCase();

        const match =
          title.includes(term) ||
          subtitle.includes(term) ||
          author.includes(term) ||
          kw.includes(term) ||
          desc.includes(term) ||
          callNo.includes(term) ||
          vol.includes(term);
        if (!match) return false;
      }

      return true;
    });
  }, [articles, articleVolFilter, articleSearchTerm]);

  // Switch to Articles tab filtered by a specific issue
  const handleViewIssueArticles = (issue) => {
    const issueCode = issue.volissuesNo || `${issue.volNo}-${issue.issueNumber}`;
    setArticleVolFilter(issueCode);
    setArticleSearchTerm('');
    setSelectedArticleDetail(null);
    setActiveTab('articles');
  };

  // Copy citation helper for an article
  const handleCopyCitation = (e, article) => {
    e.stopPropagation();
    const citation = `${article.articleAuthor || 'Unknown Author'}. "${article.articleTitle || 'Untitled'}." ${journal.journalTitle || 'Journal'} ${article.volissuesNo ? `Vol. ${article.volissuesNo}` : ''} (pp. ${article.startPageNo || '?'}-${article.endPageNo || '?'}).`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(citation);
      setCopiedCitationId(article.articleID || article.volissuesNoarticleID);
      setTimeout(() => setCopiedCitationId(null), 2000);
    }
  };

  // Helper to render issue status badge
  const renderIssueStatusBadge = (status) => {
    const s = (status || 'Available').toLowerCase();
    if (s === 'received' || s === 'available' || s === 'active') {
      return (
        <span className="badge-available">
          <i className="bi bi-check-circle-fill me-1"></i> Available in Stacks
        </span>
      );
    }
    if (s === 'expected' || s === 'pending') {
      return (
        <span className="badge-issued">
          <i className="bi bi-clock-history me-1"></i> Expected / Due
        </span>
      );
    }
    if (s === 'missing') {
      return (
        <Badge bg="danger" className="px-2 py-1">
          <i className="bi bi-exclamation-triangle-fill me-1"></i> Missing / Claimed
        </Badge>
      );
    }
    return <Badge bg="secondary" className="px-2 py-1">{status || 'Available'}</Badge>;
  };

  return (
    <div className="p-1">
      {/* Journal Title & Icon Header */}
      <div className="text-center mb-3">
        <div
          className="d-inline-flex justify-content-center align-items-center mb-2"
          style={{
            width: '68px',
            height: '68px',
            borderRadius: '16px',
            background: isBoundJournal
              ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)'
              : 'linear-gradient(135deg, #063547 0%, #0bb6d7 100%)',
            color: '#ffffff',
            fontSize: '30px',
            boxShadow: isBoundJournal
              ? '0 6px 18px rgba(2, 132, 199, 0.35)'
              : '0 6px 18px rgba(11, 182, 215, 0.35)',
          }}
        >
          <i className={isBoundJournal ? 'bi bi-journal-bookmark-fill' : 'bi bi-journal-album'}></i>
        </div>
        <div className="fw-bold fs-6 text-dark">{journal.journalTitle || 'Untitled Journal'}</div>
        {isBoundJournal && (
          <div className="mt-1">
            <span className="badge-bound-journal">
              <i className="bi bi-journal-bookmark-fill me-1"></i> Bound Journal
            </span>
          </div>
        )}
        {journal.publisher && journal.publisher !== 'NA' && (
          <div className="text-muted small mb-1">
            <i className="bi bi-building me-1"></i>{journal.publisher}
          </div>
        )}
      </div>

      {/* Navigation Tabs (Overview | Issues | Articles) */}
      <div className="vj-drawer-nav-tabs">
        <button
          type="button"
          className={`vj-drawer-tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('overview');
            setSelectedArticleDetail(null);
          }}
        >
          <i className="bi bi-info-circle"></i> Overview
        </button>

        <button
          type="button"
          className={`vj-drawer-tab-btn ${activeTab === 'issues' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('issues');
            setSelectedArticleDetail(null);
          }}
        >
          <i className="bi bi-collection"></i> Issues
          {loadingIssues ? (
            <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} />
          ) : (
            <span className="vj-tab-badge">{issues.length}</span>
          )}
        </button>

        <button
          type="button"
          className={`vj-drawer-tab-btn ${activeTab === 'articles' ? 'active' : ''}`}
          onClick={() => setActiveTab('articles')}
        >
          <i className="bi bi-file-earmark-text"></i> Articles
          {loadingArticles ? (
            <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} />
          ) : (
            <span className="vj-tab-badge">{articles.length}</span>
          )}
        </button>
      </div>

      {/* ========================================================
          TAB 1: OVERVIEW & BIBLIOGRAPHIC DETAILS
          ======================================================== */}
      {activeTab === 'overview' && (
        <div>
          {/* Periodical Shelf & Rack Location Card */}
          <div className="vj-shelf-location-card">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="small fw-bold text-muted text-uppercase">
                <i className="bi bi-geo-alt-fill text-danger me-1"></i> Periodicals Rack Location
              </span>
              <div className="d-flex align-items-center gap-1">
                {isBoundJournal && (
                  <span className="badge-bound-journal">
                    <i className="bi bi-journal-bookmark-fill me-1"></i> Bound Journal
                  </span>
                )}
                <Badge bg={journal.journalStatus === 'Active' || journal.journalStatus === 'Available' ? 'success' : 'warning'}>
                  {journal.journalStatus === 'Active' ? 'Available in Stacks' : journal.journalStatus || 'Available'}
                </Badge>
              </div>
            </div>
            <div className="vj-shelf-callno my-1">
              {journal.liblocation ? `Section: ${journal.liblocation}` : 'Periodicals & Serials Section'}
            </div>
            <div className="small text-muted">
              Journal ID: <strong>#{journal.journalID}</strong> &bull; Frequency: <strong>{journal.frequency || 'Regular'}</strong>
            </div>
          </div>

          {/* Quick Jump Action Cards (Issues & Articles) */}
          <div className="mb-3">
            <div
              className="vj-overview-quick-card"
              onClick={() => setActiveTab('issues')}
              title="Click to view all issues of this journal"
            >
              <div className="d-flex align-items-center gap-3">
                <div className="vj-quick-card-icon" style={{ background: '#e0f2fe', color: '#0284c7' }}>
                  <i className="bi bi-collection-fill"></i>
                </div>
                <div>
                  <div className="fw-bold text-dark small">Journal Issues</div>
                  <div className="text-muted small" style={{ fontSize: '0.78rem' }}>
                    {loadingIssues
                      ? 'Loading issues...'
                      : `${issues.length} Issues available on shelf / display`}
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-light text-primary border px-2 py-1">
                  {issues.length} Issues
                </span>
                <i className="bi bi-chevron-right text-muted small"></i>
              </div>
            </div>

            <div
              className="vj-overview-quick-card"
              onClick={() => setActiveTab('articles')}
              title="Click to view articles indexed in this journal"
            >
              <div className="d-flex align-items-center gap-3">
                <div className="vj-quick-card-icon" style={{ background: '#ecfdf5', color: '#059669' }}>
                  <i className="bi bi-file-earmark-richtext-fill"></i>
                </div>
                <div>
                  <div className="fw-bold text-dark small">Indexed Articles</div>
                  <div className="text-muted small" style={{ fontSize: '0.78rem' }}>
                    {loadingArticles
                      ? 'Loading articles...'
                      : `${articles.length} Research papers & articles indexed`}
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-light text-success border px-2 py-1">
                  {articles.length} Articles
                </span>
                <i className="bi bi-chevron-right text-muted small"></i>
              </div>
            </div>
          </div>

          {/* Bibliographic Details Table */}
          <div className="border rounded bg-white overflow-hidden shadow-sm mb-3">
            <Table hover size="sm" className="mb-0 align-middle">
              <tbody>
                <tr>
                  <th className="bg-light text-muted small ps-3" style={{ width: '38%' }}>Journal Title</th>
                  <td className="fw-semibold">{journal.journalTitle}</td>
                </tr>
                {journal.journalSubject && (
                  <tr>
                    <th className="bg-light text-muted small ps-3">Subject</th>
                    <td>{journal.journalSubject}</td>
                  </tr>
                )}
                {journal.frequency && (
                  <tr>
                    <th className="bg-light text-muted small ps-3">Frequency</th>
                    <td>{journal.frequency}</td>
                  </tr>
                )}
                {journal.journalType && (
                  <tr>
                    <th className="bg-light text-muted small ps-3">Format / Type</th>
                    <td>
                      {isBoundJournal ? (
                        <span className="badge-bound-journal">
                          <i className="bi bi-journal-bookmark-fill me-1"></i> Bound Journal
                        </span>
                      ) : (
                        journal.journalType
                      )}
                    </td>
                  </tr>
                )}
                {journal.publisher && journal.publisher !== 'NA' && (
                  <tr>
                    <th className="bg-light text-muted small ps-3">Publisher</th>
                    <td>{journal.publisher}</td>
                  </tr>
                )}
                {journal.issn && (
                  <tr>
                    <th className="bg-light text-muted small ps-3">ISSN</th>
                    <td>{journal.issn}</td>
                  </tr>
                )}
                {journal.liblocation && (
                  <tr>
                    <th className="bg-light text-muted small ps-3">Rack Location</th>
                    <td>{journal.liblocation}</td>
                  </tr>
                )}
                {journal.journalKeywords && journal.journalKeywords !== 'NA' && (
                  <tr>
                    <th className="bg-light text-muted small ps-3">Keywords</th>
                    <td>{journal.journalKeywords}</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Student Periodicals Tip */}
          <div className="p-2 bg-light rounded small text-muted text-center border">
            <i className="bi bi-info-circle text-primary me-1"></i>
            Current journal issues are kept on the display racks. For bound back volumes, refer to <strong>Journal ID #{journal.journalID}</strong> in the archives.
          </div>
        </div>
      )}

      {/* ========================================================
          TAB 2: JOURNAL ISSUES LIST
          ======================================================== */}
      {activeTab === 'issues' && (
        <div>
          {/* Issues Header & Quick Filter Toolbar */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="small fw-bold text-dark">
              <i className="bi bi-collection text-primary me-1"></i>
              Issues Catalogued ({issues.length})
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              className="py-0 px-2"
              style={{ fontSize: '0.78rem' }}
              onClick={() => loadIssues(journal.journalID)}
              disabled={loadingIssues}
              title="Refresh Issues"
            >
              <i className={`bi bi-arrow-clockwise ${loadingIssues ? 'spin' : ''}`}></i> Refresh
            </Button>
          </div>

          {/* Issues Search & Volume Filter Bar */}
          <div className="bg-light p-2 rounded border mb-3">
            <div className="row g-2 align-items-center">
              <div className="col-7">
                <InputGroup size="sm">
                  <InputGroup.Text className="bg-white">
                    <i className="bi bi-search text-muted"></i>
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search volume, issue #, year..."
                    value={issueSearchTerm}
                    onChange={(e) => setIssueSearchTerm(e.target.value)}
                  />
                  {issueSearchTerm && (
                    <Button variant="outline-secondary" onClick={() => setIssueSearchTerm('')}>
                      <i className="bi bi-x"></i>
                    </Button>
                  )}
                </InputGroup>
              </div>

              <div className="col-5">
                <Form.Select
                  size="sm"
                  value={issueVolumeFilter}
                  onChange={(e) => setIssueVolumeFilter(e.target.value)}
                >
                  <option value="ALL">All Volumes ({uniqueIssueVolumes.length})</option>
                  {uniqueIssueVolumes.map((vol) => (
                    <option key={vol} value={vol}>Vol. {vol}</option>
                  ))}
                </Form.Select>
              </div>
            </div>

            {/* Quick Status Filter Pills */}
            <div className="d-flex flex-wrap gap-1 mt-2 pt-2 border-top">
              <button
                type="button"
                className={`btn btn-sm py-0 px-2 ${issueStatusFilter === 'ALL' ? 'btn-primary' : 'btn-outline-secondary'}`}
                style={{ fontSize: '0.75rem', borderRadius: '12px' }}
                onClick={() => setIssueStatusFilter('ALL')}
              >
                All ({issues.length})
              </button>
              <button
                type="button"
                className={`btn btn-sm py-0 px-2 ${issueStatusFilter === 'Received' ? 'btn-success' : 'btn-outline-secondary'}`}
                style={{ fontSize: '0.75rem', borderRadius: '12px' }}
                onClick={() => setIssueStatusFilter('Received')}
              >
                <i className="bi bi-check-circle me-1"></i> Available
              </button>
              <button
                type="button"
                className={`btn btn-sm py-0 px-2 ${issueStatusFilter === 'Expected' ? 'btn-warning text-dark' : 'btn-outline-secondary'}`}
                style={{ fontSize: '0.75rem', borderRadius: '12px' }}
                onClick={() => setIssueStatusFilter('Expected')}
              >
                <i className="bi bi-clock me-1"></i> Expected
              </button>
            </div>
          </div>

          {/* Issues List Body */}
          {loadingIssues ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" role="status" />
              <div className="text-muted mt-2 small">Loading journal issues...</div>
            </div>
          ) : issuesError ? (
            <div className="text-center py-4 bg-light rounded border p-3">
              <i className="bi bi-exclamation-triangle text-warning fs-3 mb-2 d-block"></i>
              <div className="fw-semibold text-dark small">Could not load issues</div>
              <div className="text-muted small mb-2">{issuesError}</div>
              <Button size="sm" variant="outline-primary" onClick={() => loadIssues(journal.journalID)}>
                Try Again
              </Button>
            </div>
          ) : issues.length === 0 ? (
            <div className="text-center py-5 bg-white rounded border p-3">
              <i className="bi bi-journals text-muted fs-2 mb-2 d-block"></i>
              <h6 className="fw-bold text-dark">No Issues Catalogued</h6>
              <p className="text-muted small mb-0">
                No issues have been recorded yet for <strong>{journal.journalTitle}</strong>.
                Please enquire with the Serials Desk.
              </p>
            </div>
          ) : filteredIssues.length === 0 ? (
            <div className="text-center py-4 bg-white rounded border p-3">
              <i className="bi bi-funnel text-muted fs-3 mb-2 d-block"></i>
              <h6 className="fw-bold text-dark">No Matching Issues</h6>
              <p className="text-muted small mb-2">No issues matched your current search filters.</p>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => {
                  setIssueSearchTerm('');
                  setIssueStatusFilter('ALL');
                  setIssueVolumeFilter('ALL');
                }}
              >
                Reset Filters
              </Button>
            </div>
          ) : (
            <div>
              <div className="small text-muted mb-2 ps-1">
                Showing <strong>{filteredIssues.length}</strong> of <strong>{issues.length}</strong> issues
              </div>

              {filteredIssues.map((issue, idx) => {
                const statusKey = (issue.issueStatus || 'Available').toLowerCase();
                const accentBorder =
                  statusKey === 'received' || statusKey === 'available' || statusKey === 'active'
                    ? 'border-received'
                    : statusKey === 'expected' || statusKey === 'pending'
                    ? 'border-expected'
                    : statusKey === 'missing'
                    ? 'border-missing'
                    : 'border-available';

                const issueCode = issue.volissuesNo || `${issue.volNo}-${issue.issueNumber}`;
                const articleCount = articleCountByIssue[issueCode.toLowerCase()] || 0;

                return (
                  <div key={issue.volissuesNo || idx} className={`vj-sub-issue-card ${accentBorder}`}>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <div className="fw-bold text-dark" style={{ fontSize: '0.96rem' }}>
                        Vol. {issue.volNo ?? issue.volume ?? '-'}, Issue #{issue.issueNumber ?? '-'}
                      </div>
                      {renderIssueStatusBadge(issue.issueStatus)}
                    </div>

                    <div className="d-flex flex-wrap gap-2 text-muted small mb-2" style={{ fontSize: '0.8rem' }}>
                      {(issue.issueYear || issue.year) && (
                        <span>
                          <i className="bi bi-calendar3 me-1"></i>
                          {issue.issueYear || issue.year} {issue.issueMonth ? `(${issue.issueMonth})` : ''}
                        </span>
                      )}
                      {issue.pubDate && (
                        <span>
                          &bull; Pub: <strong>{issue.pubDate}</strong>
                        </span>
                      )}
                      {issue.receivedDate && (
                        <span className="text-success">
                          &bull; Recv: <strong>{issue.receivedDate}</strong>
                        </span>
                      )}
                    </div>

                    <div className="d-flex justify-content-between align-items-center pt-2 border-top mt-2">
                      <span className="font-monospace text-muted small" style={{ fontSize: '0.74rem' }}>
                        Ref: #{issueCode}
                      </span>

                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="py-1 px-2 d-flex align-items-center gap-1"
                        style={{ fontSize: '0.78rem', borderRadius: '6px' }}
                        onClick={() => handleViewIssueArticles(issue)}
                        title="View articles indexed in this volume & issue"
                      >
                        <i className="bi bi-file-earmark-text"></i>
                        <span>View Articles</span>
                        {articleCount > 0 && (
                          <Badge bg="primary" pill className="ms-1" style={{ fontSize: '0.68rem' }}>
                            {articleCount}
                          </Badge>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ========================================================
          TAB 3: ARTICLES LIST OR ARTICLE DETAIL
          ======================================================== */}
      {activeTab === 'articles' && (
        <div>
          {/* Subview: Single Article Detail */}
          {selectedArticleDetail ? (
            <div>
              {/* Back Navigation Bar */}
              <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setSelectedArticleDetail(null)}
                  className="d-flex align-items-center gap-2 fw-semibold"
                  style={{ borderRadius: '8px' }}
                >
                  <i className="bi bi-arrow-left"></i> Back to Articles ({articles.length})
                </Button>
                <span className="small text-muted font-monospace">
                  #{selectedArticleDetail.articleID || selectedArticleDetail.volissuesNoarticleID || ''}
                </span>
              </div>

              {/* Render Full Article Detail View */}
              <ArticlesOffcanvas selectedBook={selectedArticleDetail} />
            </div>
          ) : (
            /* Subview: Articles List */
            <div>
              {/* Header & Refresh */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="small fw-bold text-dark">
                  <i className="bi bi-file-earmark-richtext text-primary me-1"></i>
                  Articles Indexed ({articles.length})
                </div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="py-0 px-2"
                  style={{ fontSize: '0.78rem' }}
                  onClick={() => loadArticles(journal.journalID)}
                  disabled={loadingArticles}
                  title="Refresh Articles"
                >
                  <i className={`bi bi-arrow-clockwise ${loadingArticles ? 'spin' : ''}`}></i> Refresh
                </Button>
              </div>

              {/* Active Issue Filter Banner */}
              {articleVolFilter !== 'ALL' && (
                <div className="vj-active-filter-banner">
                  <div>
                    <i className="bi bi-funnel-fill me-1"></i>
                    Filtered by Issue: <strong>{articleVolFilter}</strong>
                  </div>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-primary fw-bold text-decoration-none"
                    onClick={() => setArticleVolFilter('ALL')}
                    title="Show all articles"
                  >
                    <i className="bi bi-x-circle me-1"></i> Clear Filter
                  </Button>
                </div>
              )}

              {/* Search & Volume Dropdown Filter Bar */}
              <div className="bg-light p-2 rounded border mb-3">
                <div className="row g-2 align-items-center">
                  <div className="col-7">
                    <InputGroup size="sm">
                      <InputGroup.Text className="bg-white">
                        <i className="bi bi-search text-muted"></i>
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="Search title, author, keyword..."
                        value={articleSearchTerm}
                        onChange={(e) => setArticleSearchTerm(e.target.value)}
                      />
                      {articleSearchTerm && (
                        <Button variant="outline-secondary" onClick={() => setArticleSearchTerm('')}>
                          <i className="bi bi-x"></i>
                        </Button>
                      )}
                    </InputGroup>
                  </div>

                  <div className="col-5">
                    <Form.Select
                      size="sm"
                      value={articleVolFilter}
                      onChange={(e) => setArticleVolFilter(e.target.value)}
                    >
                      <option value="ALL">All Volumes ({uniqueArticleVolumes.length})</option>
                      {uniqueArticleVolumes.map((vol) => (
                        <option key={vol} value={vol}>Issue: {vol}</option>
                      ))}
                    </Form.Select>
                  </div>
                </div>
              </div>

              {/* Articles List Body */}
              {loadingArticles ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" role="status" />
                  <div className="text-muted mt-2 small">Loading journal articles...</div>
                </div>
              ) : articlesError ? (
                <div className="text-center py-4 bg-light rounded border p-3">
                  <i className="bi bi-exclamation-triangle text-warning fs-3 mb-2 d-block"></i>
                  <div className="fw-semibold text-dark small">Could not load articles</div>
                  <div className="text-muted small mb-2">{articlesError}</div>
                  <Button size="sm" variant="outline-primary" onClick={() => loadArticles(journal.journalID)}>
                    Try Again
                  </Button>
                </div>
              ) : articles.length === 0 ? (
                <div className="text-center py-5 bg-white rounded border p-3">
                  <i className="bi bi-file-earmark-text text-muted fs-2 mb-2 d-block"></i>
                  <h6 className="fw-bold text-dark">No Articles Indexed</h6>
                  <p className="text-muted small mb-0">
                    No individual articles have been indexed yet for <strong>{journal.journalTitle}</strong>.
                    Articles can be indexed by library staff or requested at the reference desk.
                  </p>
                </div>
              ) : filteredArticles.length === 0 ? (
                <div className="text-center py-4 bg-white rounded border p-3">
                  <i className="bi bi-funnel text-muted fs-3 mb-2 d-block"></i>
                  <h6 className="fw-bold text-dark">No Matching Articles</h6>
                  <p className="text-muted small mb-2">No articles matched your current search filters.</p>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => {
                      setArticleSearchTerm('');
                      setArticleVolFilter('ALL');
                    }}
                  >
                    Reset Filters
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="small text-muted mb-2 ps-1">
                    Showing <strong>{filteredArticles.length}</strong> of <strong>{articles.length}</strong> articles
                  </div>

                  {filteredArticles.map((article, idx) => {
                    const articleKey = article.articleID || article.volissuesNoarticleID || idx;
                    const isCopied = copiedCitationId === articleKey;
                    const keywords = (article.articleKeywords || '')
                      .split(',')
                      .map((k) => k.trim())
                      .filter((k) => k.length > 0);

                    return (
                      <div
                        key={articleKey}
                        className="vj-sub-article-card"
                        onClick={() => setSelectedArticleDetail(article)}
                        title="Click to view complete article bibliographic record and call number"
                      >
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <span className="badge bg-light text-primary border font-monospace" style={{ fontSize: '0.72rem' }}>
                            #{article.articleID || article.volissuesNoarticleID || 'Article'}
                          </span>

                          <div className="d-flex align-items-center gap-1">
                            {article.volissuesNo && (
                              <span className="badge bg-light text-dark border" style={{ fontSize: '0.72rem' }}>
                                Vol. {article.volissuesNo}
                              </span>
                            )}
                            {article.startPageNo && (
                              <span className="badge bg-light text-secondary border" style={{ fontSize: '0.72rem' }}>
                                pp. {article.startPageNo}{article.endPageNo ? `-${article.endPageNo}` : ''}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="vj-sub-article-card-title">
                          {article.articleTitle || 'Untitled Article'}
                        </div>

                        {article.articleSubTitle && article.articleSubTitle !== 'NA' && (
                          <div className="text-muted small fst-italic mb-1" style={{ fontSize: '0.8rem' }}>
                            {article.articleSubTitle}
                          </div>
                        )}

                        <div className="text-secondary small mb-2" style={{ fontSize: '0.82rem' }}>
                          <i className="bi bi-person me-1 text-muted"></i>
                          {article.articleAuthor || 'Unknown Author'}
                        </div>

                        {article.articleDescription && (
                          <div className="text-muted small mb-2 line-clamp-2" style={{ fontSize: '0.78rem', lineHeight: '1.4' }}>
                            {article.articleDescription.length > 120
                              ? `${article.articleDescription.slice(0, 120)}...`
                              : article.articleDescription}
                          </div>
                        )}

                        {keywords.length > 0 && (
                          <div className="d-flex flex-wrap gap-1 mb-2">
                            {keywords.slice(0, 3).map((kw, ki) => (
                              <span
                                key={ki}
                                className="badge bg-light text-muted border fw-normal"
                                style={{ fontSize: '0.7rem' }}
                              >
                                #{kw}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="d-flex justify-content-between align-items-center pt-2 border-top mt-2">
                          <span className="small text-primary fw-semibold" style={{ fontSize: '0.78rem' }}>
                            View Details <i className="bi bi-arrow-right"></i>
                          </span>

                          <button
                            type="button"
                            className="vj-btn-mini-copy"
                            onClick={(e) => handleCopyCitation(e, article)}
                            title="Copy academic citation"
                          >
                            <i className={`bi ${isCopied ? 'bi-check-lg text-success' : 'bi-quote'} me-1`}></i>
                            {isCopied ? 'Copied Citation!' : 'Copy Citation'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
